import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Lock, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { LOGO_WHITE } from "@/lib/assets";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  if (!token) {
    return (
      <Layout>
        <div className="text-center" data-testid="reset-no-token">
          <div className="h-14 w-14 rounded-2xl mx-auto flex items-center justify-center"
            style={{ background: "rgba(255,59,48,0.15)", color: "#FF3B30" }}>
            <XCircle className="h-7 w-7" />
          </div>
          <h1 className="font-display text-2xl mt-5">Link non valido</h1>
          <p className="text-[#9A9A9A] mt-2 text-sm">
            Il link di reset è incompleto. Richiedine uno nuovo.
          </p>
          <Link to="/forgot-password" data-testid="reset-go-forgot"
            className="mt-6 inline-block px-5 py-3 rounded-full bg-white text-black font-semibold">
            Richiedi un nuovo link
          </Link>
        </div>
      </Layout>
    );
  }

  if (done) {
    return (
      <Layout>
        <div className="text-center" data-testid="reset-done">
          <div className="h-14 w-14 rounded-2xl mx-auto flex items-center justify-center"
            style={{ background: "rgba(52,211,153,0.15)", color: "#34D399" }}>
            <CheckCircle2 className="h-7 w-7" />
          </div>
          <h1 className="font-display text-3xl mt-5 leading-tight">
            Password aggiornata.<br/><span className="orio-grad-text">Bentornato su ORIO.</span>
          </h1>
          <Link to="/login" data-testid="reset-go-login"
            className="mt-7 block w-full text-center orio-cta-btn px-5 py-3.5 rounded-full text-black font-semibold">
            Accedi ora
          </Link>
        </div>
      </Layout>
    );
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error("Almeno 8 caratteri"); return; }
    if (password !== confirm) { toast.error("Le password non coincidono"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div data-testid="reset-page">
        <div className="text-[11px] uppercase tracking-[0.22em] text-[#9A9A9A] mb-3">Reset password</div>
        <h1 className="font-display text-3xl tracking-tight">Imposta una nuova password</h1>
        <p className="text-[#6B6B6B] mt-2 text-sm">Almeno 8 caratteri.</p>
        <form onSubmit={onSubmit} className="mt-7 space-y-4">
          <PwdField label="Nuova password" testid="reset-password" value={password} onChange={setPassword} />
          <PwdField label="Conferma password" testid="reset-confirm" value={confirm} onChange={setConfirm} />
          <button
            data-testid="reset-submit"
            type="submit" disabled={loading}
            className="orio-cta-btn relative w-full px-6 py-3.5 rounded-full font-semibold text-black overflow-hidden disabled:opacity-50">
            <span className="relative z-10">{loading ? "Salvataggio…" : "Salva nuova password"}</span>
          </button>
        </form>
      </div>
    </Layout>
  );
}

function PwdField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-[#9A9A9A]">{label}</label>
      <div className="relative mt-2">
        <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9A9A9A]" />
        <input
          data-testid={testid}
          type="password" required minLength={8}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full pl-10 pr-4 py-3 rounded-xl bg-white border border-[#F0EBE3] focus:border-white/30 outline-none"
        />
      </div>
    </div>
  );
}

function Layout({ children }) {
  return (
    <div className="min-h-screen bg-[#FBF7F1] text-[#1A1A1A] relative overflow-hidden orio-grain">
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full"
          style={{ background: "radial-gradient(circle, rgba(255,59,48,0.18) 0%, transparent 60%)" }} />
        <div className="absolute -bottom-40 -right-40 w-[600px] h-[600px] rounded-full"
          style={{ background: "radial-gradient(circle, rgba(52,211,153,0.18) 0%, transparent 60%)" }} />
      </div>
      <Link to="/" data-testid="auth-back" className="absolute top-6 left-6 z-10 flex items-center -my-1">
        <div aria-label="ORIO" className="h-7 w-[72px]"
          style={{
            background: "linear-gradient(120deg, #ff3b30 0%, #ffb020 50%, #34d399 100%)",
            backgroundSize: "200% 200%",
            animation: "orio-bg-shift 6s ease infinite",
            WebkitMask: `url(${LOGO_WHITE}) no-repeat center / contain`,
            mask: `url(${LOGO_WHITE}) no-repeat center / contain`,
          }} />
      </Link>
      <div className="relative z-10 min-h-screen flex items-center justify-center px-6">
        <div className="w-full max-w-md glass rounded-3xl p-8 sm:p-10 border border-[#F0EBE3]">
          {children}
        </div>
      </div>
    </div>
  );
}
